import { defineComponent, onMounted, ref, watch, Ref, computed } from 'vue'
import { axiosInstance } from 'src/boot/axios'

import * as ilapi from "src/composables/InleagueApiV1"
import { CoreQuestion, Guid, Registration, Season, WithDefinite } from "src/interfaces/InleagueApiV1"
import { copyViaJsonRoundTrip, exhaustiveCaseGuard, __FIXME__UNSAFE_CAST, lazyRef, vReqT, assertNonNull, unreachable, UiOption, parseIntOr, assertTruthy } from 'src/helpers/utils'
import { considerCoreQuestionDisabledForPrimaryRegistration } from "./CoreQuestions"
import { emitsDef, kVirtualQuestionAnswerPrefix, propsDef } from "./core.ilx";
import { User } from 'src/store/User'
import { Client } from 'src/store/Client'
import { FormKit } from '@formkit/vue'

export default defineComponent({
  props: propsDef(),
  emits: emitsDef(),
  setup(props, ctx) {
    const coreQuestions = lazyRef<CoreQuestion[]>()
    const seasonDetails = ref<Season | null>(null)

    type __fixme__FormData =
      & Partial<Registration> // mostly this is correct, a reg form is "some subset of those fields on a Registration object"
      & {[k: string]: any} // but we also write dynamic stuff into it
    type __fixme__keyofFormData = keyof Registration
    const formData : Ref<__fixme__FormData> = (() => {
      switch (props.variant.type) {
        case "actual-form":
          // can be an empty object if there was no existing registration
          const regCopy = ref<__fixme__FormData>(copyViaJsonRoundTrip(props.variant.maybeExistingRegistration ?? {}));

          // force coed to 0 if player is not female
          if (props.variant.playerDetails.playerGender !== "G") {
            regCopy.value.coed = 0
          }

          return regCopy;
        case "child-season-centric-preview":
          return ref({});
        case "general-form-preview":
          return ref({});
        default: exhaustiveCaseGuard(props.variant);
      }
    })();

    const ready = ref(false);

    const playerGender = computed(() => {
      return props.variant.playerDetails?.playerGender || null;
    })

    /**
     * A map of questionID->"the magic value that indicates 'show a text input'"
     */
    const magicShowTextInputValueByQuestionID = computed(() => {
      const m = new Map<Guid, string>()
      for (const q of coreQuestions.value) {
        if (q.type === "text" && q.useOptions) {
          m.set(q.id, q.useOptions.arbitraryTextInputWhenValueIs)
        }
      }
      return m;
    })

    const MaybeSectionTitle = defineComponent({
      props: {
        q: vReqT<CoreQuestion>(),
      },
      setup(props) {
        return () => {
          switch (props.q.id) {
            case "6DAAF436-AE21-4B79-81E0-BCDBE1861FBE": return <>
              <h2 class="mb-4 pb-4 border-b-4 border-t-4 pt-4 border-gray-500 font-medium text-gray-700">
                General information
              </h2>
            </>
            case "555AA59D-7F83-4002-B7BF-11DE7333DBAA": return <>
              <h2 class="mb-4 pb-4 border-b-4 border-t-4 pt-4 border-gray-500 font-medium text-gray-700">
                Medical Information
              </h2>
            </>
            case "292799AB-3D18-469F-ACE3-D758C3467FD5": return <>
              <h2 class="pt-4 border-t-4 border-gray-500 font-medium text-gray-700">
                Doctor's Contact Information
              </h2>
              <p class="mb-4 pb-4 border-b-4 border-gray-500 italic">
                If you wish your family doctor to be contacted in case of an emergency, please provide his/her name and phone number.
              </p>
            </>
            case "79229067-1A6A-454C-95BC-CFF049722D9E": return <>
              <h2 class="pt-4 border-t-4 border-gray-500 font-medium text-gray-700">Emergency Contact Information</h2>
              <p class="mb-4 pb-4 border-b-4 border-gray-500 italic">
                Please list someone who is not a parent/guardian to be contacted in an emergency, in case the family cannot be reached.
              </p>
            </>
            case "73C80599-B154-4B1A-A992-EE04F9167FCB": return <>
              <h2 class="mb-4 pb-4 border-b-4 border-t-4 pt-4 border-gray-500 font-medium text-gray-700">
                Program Registration
              </h2>
            </>
            default: return null;
          }
        }
      }
    })

    /**
     * Is this done by the backend?
     */
    const processLabel = (q: CoreQuestion) : string => {
      let processedLabel = q.label

      processedLabel = processedLabel?.replace('{leaguename}', `${Client.value.instanceConfig.shortname}`)
      processedLabel = processedLabel?.replace('{regyear}', `${seasonDetails.value?.registrationYear ?? ""}`)

      if (q.required) {
        processedLabel = `*${processedLabel}`
      }

      if (q.name === 'playerEmail') {
        processedLabel = `${processedLabel} (provide ONLY if player is in 9th grade or higher)`
      }

      return processedLabel
    }

    const createValidationParams = (q: CoreQuestion) : string => {
      const validation : string[] = []

      if (q.required) validation.push('required')
      if (q.maxlength) validation.push(`length:2,${q.maxlength}`)
      if (q.validatorFunctionName==="numericValidator") validation.push(`number`)
      if (q.validatorFunctionName==="emailValidator") validation.push(`email`)
      if (q.validatorFunctionName==="phoneNumberValidator") validation.push(`phoneNumber`)
      if (q.validatorFunctionName=="heightValidator") validation.push(`number|between:12,100`)
      if (q.validatorFunctionName=="weightValidator") validation.push(`number|between:15,400`)

      return validation.join('|')
    }

    const getCoreQuestions =  async () => {
      const qs = await (async () => {
        switch (props.variant.type) {
          case "general-form-preview":
            return await ilapi.getCoreQuestions(axiosInstance, {
              asPreview: true
            });
          case "child-season-centric-preview":
            return await ilapi.getCoreQuestions(axiosInstance, {
              asPreview: true,
              seasonUID: props.variant.seasonUID
            });
          case "actual-form":
            return await ilapi.getCoreQuestions(axiosInstance, {
              asPreview: false,
              seasonUID: props.variant.seasonUID,
              childID: props.variant.playerDetails.childID,
              competitionUIDs: props.variant.competitionUIDs,
            });
          default: exhaustiveCaseGuard(props.variant);
        }
      })();

      ctx.emit("notifyCoreQuestionsReceipt", qs);

      return qs
    }

    // currently, we own `formData`, and tell the parent component when it has changed.
    watch(formData, (val) => {
      ctx.emit('updateCoreQuestionAnswers', val)
    }, {deep: true})

    onMounted(async ()=> {
      // maybe pull in season info
      switch (props.variant.type) {
        case "actual-form":
          // fallthrough
        case "child-season-centric-preview": {
          seasonDetails.value = await Client.getSeasonByUID(props.variant.seasonUID) ?? null;
          break;
        }
        case "general-form-preview":
          // we don't have any season information with which to search for a season here
          break;
        default: exhaustiveCaseGuard(props.variant);
      }

      coreQuestions.value = await getCoreQuestions()

      // initialize form to either "current answers" (if there is an existing registration)
      // or to null if there is nothing currently.
      for (const q of coreQuestions.value) {
        switch (q.type) {
          case "html":
            break;
          case "radio":
            // fallthrough
          case "select":
            // fallthrough
          case "text":
            // fallthrough
          case "textarea":
            if (props.variant.maybeExistingRegistration && q.name in props.variant.maybeExistingRegistration) {
              // use existing value
              formData.value[q.name] = props.variant.maybeExistingRegistration[q.name as __fixme__keyofFormData]
            }
            else {
              formData.value[q.name] = null
            }
            break;
          default:
            // `type` does not possibly contain 'checkbox'?
            exhaustiveCaseGuard(q.type)
        }
      }

      // also initialize any 'virtual' values
      {
        const existingAnswers = props.variant.type === "actual-form"
          ? (props.variant.maybeExistingRegistration ?? {}) // might not have existing answers, that's OK
          : {} // will never have existing answers in this case, that's OK too

        formData.value = {
          ...formData.value,
          ...defaultVirtualValues(coreQuestions.value, existingAnswers)
        };
      }

      ready.value = true;
    })

    const OneFormElem = defineComponent({
      props: {
        q: vReqT<CoreQuestion>(),
        maybeExistingRegistration: vReqT<Registration | null>(),
      },
      setup(props) {
        const options = computed(() => {
          return Array.isArray(props.q.options)
            ? props.q.options.map((opt) : UiOption => {
              return {
                label: opt.label,
                value: opt.value,
                attrs: props.q.type === "radio"
                  ? {"data-test": props.q.id} // some tests rely on this
                  : undefined,
              }
            })
            : []
        });

        const disabled = computed(() => {
          return props.maybeExistingRegistration
            ? considerCoreQuestionDisabledForPrimaryRegistration(props.maybeExistingRegistration, props.q, {checkAgainstUserRoles: User.value.roles})
            : false;
        })

        const shouldSkip = computed(() => {
          const SKIP = true;
          const NOSKIP = false;

          switch (props.q.name) {
            case "mediProbs" satisfies __fixme__keyofFormData: {
              // don't show "medical problems" text input if "has medical some condition" is false
              const hasDeclaredMedicalCondition = formData.value.medCond /*weakEq*/ == 1;
              return hasDeclaredMedicalCondition ? NOSKIP
                : SKIP;
            }
            case "coed" satisfies __fixme__keyofFormData: {
              // playerGender is not a form value, i.e. not dynamic, it's static against the "currently registering player"
              return playerGender.value == 'G' ? NOSKIP // girl players get "coed question"
                : SKIP // otherwise skip it
            }
            case "playerEmail" satisfies __fixme__keyofFormData: {
              const grade = parseIntOr(formData.value.grade, null)
              return grade === null ? SKIP // no (or invalid) input
                : grade < 9 ? SKIP // 8th graders and below don't have email addresses collected
                : NOSKIP // otherwise, offer to collect email addr
            }
            default: return NOSKIP
          }
        })

        /**
         * formkit won't track data that doesn't exist unlike typical vue forms, so
         * we sanity check that particular fields have at least a non-undefined mapping
         * (i.e. to an empty string or empty array or whatever is appropriate, basically
         * so long as it is not-undefined formkit should track the values.)
         */
        const assertFieldIsDefined = (fieldName: string) => {
          assertTruthy(formData.value[fieldName] !== undefined);
        }

        return () => {
          if (shouldSkip.value) {
            return null;
          }

          switch (props.q.type) {
            case "html": return <p v-html={props.q.html || ""} data-test={props.q.id}/>
            case "radio": {
              assertFieldIsDefined(props.q.name)

              return <div>
                <FormKit
                  type="radio"
                  options={options.value}
                  v-model={formData.value[props.q.name]}
                  onInput={(fresh: any) => {
                    if (formData.value[props.q.name] /*weakEq*/ == fresh) {
                      return;
                    }

                    if (props.q.name === "medCond" satisfies __fixme__keyofFormData) {
                      if (formData.value.medCond /*weakEq*/ == 1 && fresh /*weakEq*/ == 0) {
                        // clear mediProbs if medCond is flipped to false
                        formData.value.mediProbs = ""
                      }
                    }
                  }}

                  options-class="flex flex-row"
                  option-class="pr-4"

                  label={processLabel(props.q)}
                  validation={createValidationParams(props.q)}
                  validationLabel={props.q.name}
                  autocomplete="off"
                  data-test={props.q.id}
                  disabled={disabled.value}
                />
              </div>
            }
            case "select": {
              assertFieldIsDefined(props.q.name)

              return <FormKit
                type="select"
                placeholder="Select an Option"
                options={options.value}
                v-model={formData.value[props.q.name]}

                label={processLabel(props.q)}
                validation={createValidationParams(props.q)}
                validationLabel={props.q.name}
                autocomplete="off"
                data-test={props.q.id}
                disabled={disabled.value}
              />
            }
            case "text": {
              const justText = (() => {
                if (props.q.useOptions) {
                  const hasNoOptions = props.q.useOptions.options.length === 0
                  const has1OptionButItIsJustUseArbitraryText = props.q.useOptions.options.length === 1 && props.q.useOptions.options[0].value === props.q.useOptions.arbitraryTextInputWhenValueIs

                  const isPlayerSchoolQ = props.q.name === "playerSchool"
                  const playerSchoolOptionsDisabled = Client.value.instanceConfig.schoolselect /*weakEq*/ == false

                  const justText = hasNoOptions || has1OptionButItIsJustUseArbitraryText || (isPlayerSchoolQ && playerSchoolOptionsDisabled)
                  return justText;
                }
                else {
                  return true;
                }
              })();

              if (justText) {
                assertFieldIsDefined(props.q.name)

                return <FormKit
                  type="text"
                  v-model={formData.value[props.q.name]}
                  name={props.q.name}
                  preserve={false}

                  label={processLabel(props.q)}
                  validation={createValidationParams(props.q)}
                  validationLabel={props.q.name}
                  autocomplete="off"
                  data-test={props.q.id}
                  disabled={disabled.value}
                />
              }
              else {
                assertNonNull(props.q.useOptions) // justText=false implies there are options

                // need both the virtual and non-virtual pair here
                assertFieldIsDefined(props.q.name)
                assertFieldIsDefined(fkNodeNameVirtual(props.q))

                return <>
                  <FormKit
                    type="select"
                    options={props.q.useOptions.options}
                    placeholder="Select an Option"
                    v-model={formData.value[fkNodeNameVirtual(props.q)]}
                    onInput={(v: any) => {
                      if (typeof v !== "string") {
                        unreachable()
                      }

                      const virtual = fkNodeNameVirtual(props.q)
                      if (formData.value[virtual] === v) {
                        return
                      }

                      formData.value[virtual] = v; // update 'virtual' field

                      if (v === magicShowTextInputValueByQuestionID.value.get(props.q.id)) {
                        // transition to the "show the text input instead of the select"
                        formData.value[props.q.name] = "" // set 'actual' value to empty string, to be edited by now-appearing text input
                      }
                      else {
                        // some real option is selected
                        formData.value[props.q.name] = v // set 'actual' value to selected value
                      }
                    }}

                    label={processLabel(props.q)}
                    validation={createValidationParams(props.q)}
                    validationLabel={props.q.name}
                    autocomplete="off"
                    data-test={props.q.id}
                    disabled={disabled.value}
                  />
                  {formData.value[fkNodeNameVirtual(props.q)] /*weakEq*/ == props.q.useOptions.arbitraryTextInputWhenValueIs
                    ? <FormKit
                      type="text"
                      v-model={formData.value[props.q.name]} // write directly to 'actual' value

                      label={undefined}
                      validation={createValidationParams(props.q)}
                      validationLabel={props.q.name}
                      autocomplete="off"
                      data-test={props.q.id} // tests use locator `input[data-test=...]`
                      preserve={true} // don't delete the value out from under us just because the element unmounted
                      disabled={disabled.value}
                    />
                    : null}
                </>
              }
            }
            case "textarea":
              assertFieldIsDefined(props.q.name)
              return <FormKit
                type="textarea"
                v-model={formData.value[props.q.name]}

                label={processLabel(props.q)}
                validation={createValidationParams(props.q)}
                validationLabel={props.q.name}
                autocomplete="off"
                data-test={props.q.id}
                disabled={disabled.value}
              />
            default: exhaustiveCaseGuard(props.q.type)
          }
        }
      }
    })

    return () => {
      if (!ready.value) {
        return null
      }

      return <div data-test="core-questions-form">
        {coreQuestions.value.map(q => {
          return <>
            <MaybeSectionTitle q={q}/>
            <OneFormElem
              q={q}
              maybeExistingRegistration={props.variant.maybeExistingRegistration ?? null}
            />
          </>
        })}
      </div>
    }
  },
})

function fkNodeName(q: CoreQuestion) : string {
  return q.name
}

function fkNodeNameVirtual(q: CoreQuestion) : string {
  return kVirtualQuestionAnswerPrefix + fkNodeName(q)
}

function needsVirtualField(q: CoreQuestion) : q is WithDefinite<CoreQuestion, "useOptions"> {
  // We need a "virtual" field to track the selected option in addition to the "actual" value,
  // where the selected option is usually exactly the same as the actual value, EXCEPT when
  // the selected option is the "show a text input" value, and then the actual value comes from
  // a text input
  return q.type === "text" && !!q.useOptions
}

function defaultVirtualValues(coreQuestions: CoreQuestion[], existingAnswers: Partial<Registration>) {
  const qsNeedingVirtualFields = coreQuestions.filter(needsVirtualField);

  const attrs : {[virtualNodeName: string]: any} = {}

  for (const q of qsNeedingVirtualFields) {
    const currentAnswer = existingAnswers[q.name as keyof Registration]
    const magicShowTheTextInputValue = q.useOptions.arbitraryTextInputWhenValueIs
    const has1OptionButItIsJustUseArbitraryText = q.useOptions.options.length === 1 && q.useOptions.options[0].value === q.useOptions.arbitraryTextInputWhenValueIs

    if (currentAnswer === undefined) {
      if (q.useOptions.options.length > 0 && !has1OptionButItIsJustUseArbitraryText) {
        attrs[fkNodeNameVirtual(q)] = ""
        attrs[fkNodeName(q)] = ""
      }
      else {
        attrs[fkNodeNameVirtual(q)] = magicShowTheTextInputValue
        attrs[fkNodeName(q)] = ""
      }
    }
    else if (q.useOptions.options.find(v => v.value === currentAnswer)) {
      attrs[fkNodeNameVirtual(q)] = currentAnswer
      attrs[fkNodeName(q)] = currentAnswer
    }
    else {
      attrs[fkNodeNameVirtual(q)] = magicShowTheTextInputValue
      attrs[fkNodeName(q)] = currentAnswer
    }
  }

  return attrs;
}
